import React, { useContext, useEffect, useMemo, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ReactApexChart from 'react-apexcharts'; // Corrected import
import { clockInOutUser } from '../../../../service/UserDashboard';
import AppContext from '../../../../context/AppContext';
import translations from '../../../common/Translations';
import ChartModal from './ChartModal';
import { MdSupportAgent } from 'react-icons/md';
import CommonButton from '../../../common/CommonButton';
import ActionIconButton from '../../../common/ActionIconButton';
import ModalCloseButton from '../../../common/ModalCloseButton';
import datePickerPopper from '../../../common/DatePickerPopper';
import { BarChart3, CalendarDays, Eye } from 'lucide-react';

const parseYmd = (s) => {
    if (!s) return null;
    const p = String(s).split("-").map(Number);
    if (p.length < 3 || p.some((n) => Number.isNaN(n))) return null;
    return new Date(p[0], p[1] - 1, p[2]);
};
const formatYmd = (d) => {
    if (!d) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const ClockInOut = ({ selectedOrgId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clockData, setClockData] = useState({ 'clock-in': 0, 'clock-out': 0 });
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
    const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
    const [startDate, setStartDate] = useState(startOfDay.toISOString().substring(0, 10));
    const [endDate, setEndDate] = useState(endOfDay.toISOString().substring(0, 10));
    const [showDateModal, setShowDateModal] = useState(false);
    const [showModal, setShowModal] = useState(false); // State for chart type modal
    const [chartView, setChartView] = useState("area"); // Default chart view
    const { theme, langMode } = useContext(AppContext);
    const todayPicker = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const defaultColors = ["#673ab7", "#00897b", "#ffb300"]; // Deeper shades for default
    const darkThemeColors = ["#9c27b0", "#26a69a", "#ffca28"]; // Lighter shades for dark theme
    const highContrastColors = ["#7b1fa2", "#00695c", "#ff8f00"]; // Stronger contrast

    const getChartColors = () => {
        if (theme === "dark") return darkThemeColors;
        if (theme === "high-contrast") return highContrastColors;
        return defaultColors;
    };

    const handleClockInOut = async () => {
        setLoading(true);
        setError(null);
        try {
            const start = new Date(`${startDate}T00:00:00`);
            const end = new Date(`${endDate}T23:59:59`);
            const response = await clockInOutUser(selectedOrgId, start.toISOString(), end.toISOString());
            if (response?.body?.value) {
                setClockData(response.body.value);
            } else {
                setClockData({ 'clock-in': 0, 'clock-out': 0 });
                console.warn("API response missing expected values.");
            }
        } catch (err) {
            setError(err.message);
            setClockData({ 'clock-in': 0, 'clock-out': 0 });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedOrgId) {
            handleClockInOut();
        }
    }, [selectedOrgId, startDate, endDate]);

    const categories = ['Clock In', 'Clock Out'];
    const seriesData = [clockData['clock-in'], clockData['clock-out']];
    const totalClockEntries = seriesData.reduce((sum, val) => sum + val, 0);
    const normalizedSeriesData = totalClockEntries > 0
        ? seriesData.map(val => (val / totalClockEntries) * 100)
        : [0, 0];

    const isDataEmpty = totalClockEntries === 0;

    const baseChartOptions = {
        chart: {
            toolbar: {
                show: false,
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            },
            foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Global text color
        },
        dataLabels: {
            enabled: true,
            style: {
                fontSize: "12px",
                colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#fff")], // Data label color
            },
        },
        legend: {
            show: true,
            position: "bottom",
            fontSize: "13px",
            labels: {
                colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Legend label color
            },
            itemMargin: {
                horizontal: 10,
                vertical: 5
            }
        },
        tooltip: {
            enabled: true,
            theme: theme === "dark" ? "dark" : "light",
            style: {
                fontSize: "13px",
                fontFamily: "inherit",
                background: theme === "dark" ? "#333" : theme === "high-contrast" ? "#FFF" : "#f4f4f4",
                color: theme === "dark" ? "#fff" : theme === "high-contrast" ? "#000" : "#fff",
            },
            y: {
                formatter: function (val, { seriesIndex }) {
                    return seriesData[seriesIndex] + " entries";
                }
            }
        },
        colors: getChartColors(), // Dynamic colors
    };

    const RadialBarChartView = ({ series, labels }) => {
        const radialBarOptions = {
            ...baseChartOptions,
            chart: {
                ...baseChartOptions.chart,
                type: "radialBar",
            },
            plotOptions: {
                radialBar: {
                    offsetY: 0,
                    startAngle: 0,
                    endAngle: 270,
                    hollow: {
                        margin: 5,
                        size: '30%', // Adjust size to create concentric effect
                        background: 'transparent',
                        image: undefined,
                    },
                    track: {
                        show: true,
                        startAngle: undefined,
                        endAngle: undefined,
                        background: theme === "dark" ? "#374151" : "#e0e0e0", // Track color for un-filled part
                        strokeWidth: '97%', // Make track thinner to create gap
                        opacity: 1,
                        margin: 10, // Margin between each ring
                        dropShadow: {
                            enabled: true,
                            top: 2,
                            left: 0,
                            blur: 4,
                            opacity: 0.15
                        }
                    },
                    dataLabels: {
                        show: true,
                        name: {
                            show: true,
                            fontSize: '16px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            color: baseChartOptions.chart.foreColor,
                            offsetY: -10
                        },
                        value: {
                            show: true,
                            fontSize: '18px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 700,
                            color: baseChartOptions.chart.foreColor,
                            offsetY: 5,
                            formatter: function (val) {
                                return `${Number.isFinite(val) ? val.toFixed(0) : 0}%`;
                            }
                        },
                        total: {
                            show: true,
                            label: translations[langMode].totalEntries || "Total Entries",
                            color: baseChartOptions.chart.foreColor,
                            formatter: function (w) {
                                return totalClockEntries; // Use derived total
                            }
                        }
                    }
                }
            },
            fill: {
                type: 'gradient',
                gradient: {
                    shade: 'dark',
                    type: 'horizontal',
                    shadeIntensity: 0.5,
                    gradientToColors: getChartColors(), // Ensure gradients use the defined colors
                    inverseColors: true,
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100]
                }
            },
            labels: labels, // Labels for each series (outer ring)
            stroke: {
                lineCap: 'round' // Rounded ends for the bars
            },
            legend: {
                show: true, // Keep legend for clarity
                position: 'bottom',
                offsetY: 0,
                height: 50,
                markers: {
                    fillColors: getChartColors()
                }
            },
        };

        return (
            <div id="user-radial-bar-chart" className="w-full h-full flex justify-center items-center">
                <ReactApexChart
                    options={radialBarOptions}
                    series={series} // Use normalized data for percentages
                    type="radialBar"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    };

    const DonutChartView = ({ series, labels }) => {
        const donutOptions = {
            ...baseChartOptions,
            labels: labels,
            dataLabels: {
                ...baseChartOptions.dataLabels,
                formatter: function (val, opts) {
                    const total = opts.w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                    return total > 0 ? `${val.toFixed(1)}%` : '';
                }
            },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%',
                        labels: {
                            show: true,
                            name: {
                                show: true,
                                fontSize: "14px",
                                color: baseChartOptions.chart.foreColor,
                            },
                            value: {
                                show: true,
                                fontSize: "18px",
                                fontWeight: 600,
                                color: baseChartOptions.chart.foreColor,
                                formatter: function (val) {
                                    return val;
                                },
                            },
                            total: {
                                show: true,
                                showAlways: true,
                                label: translations[langMode].totalEntries || "Total Entries",
                                fontSize: '14px',
                                fontWeight: 600,
                                color: baseChartOptions.chart.foreColor,
                                formatter: function (w) {
                                    return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                                }
                            },
                        },
                    },
                },
            },
            stroke: {
                show: true,
                colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")],
                width: 2
            },
            tooltip: {
                y: {
                    formatter: function (val) {
                        return val + " entries";
                    }
                }
            }
        };

        return (
            <div id="user-donut-chart" className="w-full h-full flex justify-center items-center">
                <ReactApexChart
                    options={donutOptions}
                    series={series}
                    type="donut"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    };

    const BarChartView = ({ series, categories }) => {
        const barOptions = {
            ...baseChartOptions,
            chart: {
                ...baseChartOptions.chart,
                type: "bar",
                background: theme === "dark" ? "#1F2937" : "#FFFFFF",
            },
            xaxis: {
                categories: categories,
                labels: {
                    style: {
                        colors: baseChartOptions.chart.foreColor,
                        fontSize: '13px', // Slightly larger font size for labels
                        fontWeight: 500,
                    },
                },
                axisBorder: {
                    show: true,
                    color: theme === "dark" ? "#4A5568" : "#E2E8F0",
                },
                axisTicks: {
                    show: true,
                    color: theme === "dark" ? "#4A5568" : "#E2E8F0",
                },
            },
            yaxis: {
                labels: {
                    style: {
                        colors: baseChartOptions.chart.foreColor,
                        fontSize: '13px', // Slightly larger font size for labels
                        fontWeight: 500,
                    },
                    formatter: function (val) {
                        return Math.floor(val); // Ensure whole numbers for user counts
                    }
                },
                axisBorder: {
                    show: false,
                },
                axisTicks: {
                    show: false,
                },
                title: {
                    text: translations[langMode].numberOfEntries || "Number of Entries",
                    style: {
                        color: baseChartOptions.chart.foreColor,
                        fontSize: '14px',
                        fontWeight: 600,
                    }
                }
            },
            plotOptions: {
                bar: {
                    horizontal: false,
                    borderRadius: 8, // More rounded corners for a softer look
                    columnWidth: '45%', // Slightly thinner columns
                    distributed: true, // Distribute colors across categories
                    dataLabels: {
                        position: 'top',
                        offsetY: -20, // Adjust offset to prevent overlap
                    },
                },
            },
            dataLabels: {
                enabled: true,
                offsetY: -20, // Position data labels above the bars
                style: {
                    fontSize: '13px',
                    colors: [theme === "dark" ? "#FFF" : "#333"], // Ensure good contrast
                    fontWeight: 600,
                },
                background: {
                    enabled: true,
                    foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
                    padding: 6,
                    borderRadius: 4,
                    borderWidth: 1,
                    borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                    opacity: 0.9,
                    dropShadow: {
                        enabled: true,
                        top: 1,
                        left: 1,
                        blur: 2,
                        opacity: 0.2,
                    }
                },
                formatter: function (val) {
                    return val; // Display the raw count
                }
            },
            grid: {
                show: true,
                borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                strokeDashArray: 4, // Dashed grid lines
                position: 'back',
                xaxis: {
                    lines: {
                        show: false // No vertical grid lines for cleaner look
                    }
                },
                yaxis: {
                    lines: {
                        show: true // Keep horizontal grid lines for readability
                    }
                },
                padding: {
                    left: 0,
                    right: 0
                }
            },
            tooltip: {
                enabled: true,
                y: {
                    formatter: function (val) {
                        return val + " entries";
                    }
                },
                x: {
                    formatter: function (val) {
                        return val;
                    }
                },
            },
            colors: getChartColors(), // Apply dynamic colors
            legend: {
                show: false // Hide legend for single series bar chart
            },
            responsive: [{
                breakpoint: 768, // Adjust for medium screens and smaller
                options: {
                    plotOptions: {
                        bar: {
                            columnWidth: '60%',
                        }
                    },
                    dataLabels: {
                        offsetY: -15,
                        style: {
                            fontSize: '11px',
                        }
                    },
                    xaxis: {
                        labels: {
                            style: {
                                fontSize: '11px',
                            }
                        }
                    },
                    yaxis: {
                        labels: {
                            style: {
                                fontSize: '11px',
                            }
                        },
                        title: {
                            style: {
                                fontSize: '12px',
                            }
                        }
                    }
                }
            },
            {
                breakpoint: 480, // Adjust for small screens
                options: {
                    plotOptions: {
                        bar: {
                            columnWidth: '70%',
                        }
                    },
                    dataLabels: {
                        offsetY: -10,
                        style: {
                            fontSize: '10px',
                        }
                    },
                    xaxis: {
                        labels: {
                            style: {
                                fontSize: '10px',
                            }
                        }
                    },
                    yaxis: {
                        labels: {
                            style: {
                                fontSize: '10px',
                            }
                        },
                        title: {
                            style: {
                                fontSize: '11px',
                            }
                        }
                    }
                }
            }
            ]
        };

        return (
            <div id="user-bar-chart" className="w-full h-full "> {/* Added padding */}
                <ReactApexChart
                    options={barOptions}
                    series={[{ name: translations[langMode].entryCount || "Entry Count", data: series }]}
                    type="bar"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    };

    const AreaChartView = ({ series, categories }) => {
        const areaOptions = {
            ...baseChartOptions,
            chart: {
                ...baseChartOptions.chart,
                type: "area",
            },
            xaxis: {
                categories: categories,
                labels: {
                    style: {
                        colors: baseChartOptions.chart.foreColor,
                    },
                },
                tooltip: {
                    enabled: false
                }
            },
            yaxis: {
                labels: {
                    style: {
                        colors: baseChartOptions.chart.foreColor,
                    },
                },
            },
            stroke: {
                curve: "smooth",
                width: 3,
            },
            fill: {
                type: "gradient",
                gradient: {
                    shadeIntensity: 1,
                    opacityFrom: 0.7,
                    opacityTo: 0.9,
                    stops: [0, 90, 100]
                }
            },
            dataLabels: {
                enabled: true,
                background: {
                    enabled: true,
                    foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
                    padding: 4,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                    opacity: 0.9,
                },
            },
            grid: {
                show: true,
                borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                strokeDashArray: 4,
                position: 'back'
            },
            legend: {
                show: false
            }
        };

        return (
            <div id="user-area-chart" className="w-full h-full ">
                <ReactApexChart
                    options={areaOptions}
                    series={[{ name: translations[langMode].entryCount || "Entry Count", data: series }]}
                    type="area"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    };

    const RadarChartView = ({ series, categories }) => {
        const radarOptions = {
            ...baseChartOptions,
            chart: {
                ...baseChartOptions.chart,
                type: "radar",
            },
            xaxis: {
                categories: categories,
                labels: {
                    style: {
                        colors: baseChartOptions.chart.foreColor,
                    },
                },
            },
            yaxis: {
                show: false,
            },
            stroke: {
                show: true,
                width: 2,
                curve: 'smooth',
                lineCap: 'round',
            },
            fill: {
                opacity: 0.4,
            },
            markers: {
                size: 5,
                hover: {
                    size: 7,
                },
                colors: getChartColors(),
                strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
                strokeWidth: 2,
            },
            dataLabels: {
                enabled: true,
                style: {
                    fontSize: '12px',
                    colors: [baseChartOptions.chart.foreColor]
                },
                background: {
                    enabled: true,
                    foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
                    padding: 4,
                    borderRadius: 2,
                    borderWidth: 1,
                    borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                    opacity: 0.9,
                },
            },
            grid: {
                show: true,
                borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
                strokeDashArray: 4,
                position: 'back',
                xaxis: {
                    lines: {
                        show: true
                    }
                },
                yaxis: {
                    lines: {
                        show: true
                    }
                },
                padding: {
                    left: 20,
                    right: 20
                }
            },
            legend: {
                show: true
            }
        };

        return (
            <div id="user-radar-chart" className="w-full h-full">
                <ReactApexChart
                    options={radarOptions}
                    series={[{ name: translations[langMode].entryCount || "Entry Count", data: series }]}
                    type="radar"
                    width="100%"
                    height="100%"
                />
            </div>
        );
    };

    // harshit vachhani  - resolved error (TypeError: Cannot read properties of undefined (reading 'x'))
 
    const isSeriesInvalid =
  !Array.isArray(seriesData) ||
  seriesData.length === 0 ||
  seriesData.some(v => v == null || Number.isNaN(v));


    return (
        <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"} p-2`}>
            <div className="flex w-full items-center justify-between gap-2 py-1">
                <h2 className="flex min-w-0 flex-1 items-center gap-1.5 text-left text-xs font-semibold sm:gap-2 sm:text-base">
                    <MdSupportAgent size={18} className="shrink-0 text-purple-500 sm:h-5 sm:w-5" />
                    <label className="mt-0.5 truncate">{translations[langMode].fieldAgentCLock}</label>
                </h2>

                <div className="flex shrink-0 items-center gap-1">
                    <ActionIconButton
                        type="button"
                        tone="gray"
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        onClick={() => setShowDateModal(true)}
                        aria-label={translations[langMode].RangeFilter}
                        tooltipText={translations[langMode].RangeFilter}
                        tooltipPosition="bottom"
                    >
                        <CalendarDays size={18} />
                    </ActionIconButton>
                    <ActionIconButton
                        type="button"
                        tone="gray"
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        onClick={() => setShowModal(true)}
                        aria-label={translations[langMode].ViewChart}
                        tooltipText={translations[langMode].ViewChart}
                        tooltipPosition="bottom"
                    >
                        <Eye size={18} />
                    </ActionIconButton>
                </div>
            </div>

            {(isDataEmpty || isSeriesInvalid) ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
                    <span
                        className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                                ? "bg-[#236DB4]/20 text-[#236DB4]"
                                : theme === "high-contrast"
                                    ? "bg-black text-yellow-300"
                                    : "bg-[#236DB4]/10 text-[#236DB4]"
                            }`}
                    >
                        <BarChart3 className="h-6 w-6" />
                    </span>
                    <div className="flex flex-col gap-1">
                        <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
                            {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
                        </p>
                        <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
                            {translations[langMode].chartWaitingForResponses}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="h-full min-h-[220px] w-full flex-1 sm:min-h-[300px]">
                    {chartView === "chart" ? (
                        <RadialBarChartView series={normalizedSeriesData} labels={categories} />
                    ) : chartView === "donut" ? (
                        <DonutChartView series={seriesData} labels={categories} />
                    ) : chartView === "area" ? (
                        <AreaChartView series={seriesData} categories={categories} />
                    ) : chartView === "radar" ? (
                        <RadarChartView series={seriesData} categories={categories} />
                    ) : (
                        <BarChartView series={seriesData} categories={categories} />
                    )}
                </div>
            )}

            {showDateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div data-tooltip-bounds className="relative max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-800">
                        <div className="mb-4 pe-10">
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{translations[langMode].selectDateRange}</h3>
                        </div>
                        <ModalCloseButton
                            className="absolute end-4 top-4"
                            onClick={() => setShowDateModal(false)}
                        />
                        <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="min-w-0">
                            <label className="text-sm font-medium">{translations[langMode].startDate}</label>
                            <DatePicker
                                wrapperClassName="w-full"
                                selected={parseYmd(startDate)}
                                onChange={(date) => {
                                    setStartDate(date ? formatYmd(date) : "");
                                    if (date && parseYmd(endDate) && parseYmd(endDate) < date) {
                                        setEndDate(formatYmd(date));
                                    }
                                }}
                                placeholderText={translations[langMode].startDate}
                                maxDate={todayPicker}
                                selectsStart
                                startDate={parseYmd(startDate)}
                                endDate={parseYmd(endDate)}
                                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                                calendarClassName="!rounded-lg shadow-lg"
                                popperPlacement="bottom-start"
                                popperContainer={datePickerPopper}
                            />
                        </div>
                        <div className="min-w-0">
                            <label className="text-sm font-medium">{translations[langMode].endDate}</label>
                            <DatePicker
                                wrapperClassName="w-full"
                                selected={parseYmd(endDate)}
                                onChange={(date) => setEndDate(date ? formatYmd(date) : "")}
                                placeholderText={translations[langMode].endDate}
                                minDate={parseYmd(startDate)}
                                maxDate={todayPicker}
                                selectsEnd
                                startDate={parseYmd(startDate)}
                                endDate={parseYmd(endDate)}
                                disabled={!startDate}
                                className={`w-full h-9 px-2 !rounded-lg text-sm border border-gray-300 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${theme === "dark" ? "dark-mode border-gray-200 placeholder-white" : theme === "high-contrast" ? "high-contrast" : ""}`}
                                calendarClassName="!rounded-lg shadow-lg"
                                popperPlacement="bottom-start"
                                popperContainer={datePickerPopper}
                            />
                        </div>
                        </div>
                        <div className="flex justify-end gap-3">
                            <CommonButton colorIntent="danger"
                                variant='danger'
                                onClick={() => setShowDateModal(false)}
                                tooltipText={translations[langMode].cancelandclose}
                                tooltipPosition="top"
                            >
                                {translations[langMode].cancle}
                            </CommonButton>
                            <CommonButton colorIntent="primary"
                                onClick={() => {
                                    setShowDateModal(false);
                                    // handleClockInOut(); // redundant: date
                                    // pickers call setStartDate/setEndDate
                                    // directly, and the effect (deps:
                                    // [selectedOrgId, startDate, endDate])
                                    // already re-fetches on that change.
                                }}
                                variant='primary'
                                tooltipText={translations[langMode].applytheselecteddaterange}
                                tooltipPosition="top"
                            >
                                {translations[langMode].apply}
                            </CommonButton>
                        </div>
                    </div>
                </div>
            )}

            <ChartModal
                showModal={showModal}
                setShowModal={setShowModal}
                setChartView={setChartView}
                currentChartView={chartView}
                availableChartTypes={["radialBar", "donut", "bar", "area", "radar"]}
            />

        </div >
    );
};

export default ClockInOut;
